// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE STAGE PAGES' SHARED PIECES (dashboard stages spec, Jesse/Hannah 2026-11-03 — section B, all stages).
//   · PrimaryButton / SecondaryButton — normal size, never full width (B4).
//   · NumberedChecklist — ONE list style everywhere: numbered, because order matters (B3). The current item (the first
//     the borrower can act on — constants/journeySteps waitingIndex) gets a BLUE step number and a small "Next" tag; no
//     light-blue wrapper card. Done → a green tick; overdue → red only on its due label (the colour rule). Doorly's own
//     tasks are plain status lines (kind 'status', not numbered — B2); a note (kind 'note') is a plain paragraph.
//   · HelpCard — the ONE help card per page (B5): "Have questions?", the approved line with the Help Center link,
//     "Message Doorly" and "Call support" (normal size).
// Brand colours only: Doorly blue #3a6ad9 (tailwind doorly-*), statusColors green / red; slate text on white only.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { CHECK_COLOURS, JOURNEY_COPY, waitingIndex, DASHBOARD_ROUND2_WORDING_APPROVED, ROUND2_COPY } from '../../constants/journeySteps';
import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from '../../constants/support';

export const BLUE = '#3a6ad9';
export const SUPPORT_TEL = SUPPORT_PHONE_TEL;   // ONE source: constants/support (round 2, 2026-11-04)

const BTN = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '10px 18px', fontSize: 14, fontWeight: 700,
  borderRadius: 10, cursor: 'pointer', textDecoration: 'none', lineHeight: 1.3, maxWidth: '100%' };

export function PrimaryButton({ children, onClick, disabled = false, testId, href, type = 'button', ...rest }) {
  const style = { ...BTN, background: disabled ? '#e5e7eb' : BLUE, color: disabled ? '#6b7280' : '#fff', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer' };
  if (href) return <a href={href} data-testid={testId} style={style} {...rest}>{children}</a>;
  return <button type={type} data-testid={testId} onClick={onClick} disabled={disabled} aria-disabled={disabled ? 'true' : undefined} style={style} {...rest}>{children}</button>;
}

export function SecondaryButton({ children, onClick, disabled = false, testId, href, target, rel, ...rest }) {
  const style = { ...BTN, background: '#fff', color: disabled ? '#9ca3af' : BLUE, border: `1px solid ${disabled ? '#e5e7eb' : BLUE}`, cursor: disabled ? 'not-allowed' : 'pointer' };
  if (href) return <a href={href} target={target} rel={rel} data-testid={testId} onClick={onClick} style={style} {...rest}>{children}</a>;
  return <button type="button" data-testid={testId} onClick={onClick} disabled={disabled} aria-disabled={disabled ? 'true' : undefined} style={style} {...rest}>{children}</button>;
}

export function QuietLink({ children, onClick, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      style={{ padding: 0, background: 'none', border: 'none', color: '#4b5563', fontSize: 13, textDecoration: 'underline', cursor: 'pointer', textAlign: 'left' }}>
      {children}
    </button>
  );
}

export function ButtonRow({ children, style }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', ...style }}>{children}</div>;
}

/** A white card with the checklist's title. */
export function CardFrame({ title, children, testId, id }) {
  return (
    <div id={id} data-testid={testId} className="bg-white rounded-2xl shadow-sm border border-gray-100" style={{ padding: 22, marginBottom: 16 }}>
      {title ? <div style={{ fontSize: 16, fontWeight: 800, color: '#000', marginBottom: 14 }}>{title}</div> : null}
      {children}
    </div>
  );
}

/**
 * items: [{ id, title, description, content, done, optional, overdue, dueLabel, actionable, kind, locked }]
 *   locked — round 2 (2026-11-04): greyed, never current, no due label (the down payment before the contract is signed).
 *   kind 'status' — Doorly's task, a plain line (not numbered); kind 'note' — a plain paragraph.
 * waiting: override of the current item's index (else waitingIndex over the numbered items); -1 → none.
 */
export function NumberedChecklist({ items, waiting = undefined, testId = 'dashboard-checklist', title = 'Your checklist', id = 'dashboard-checklist' }) {
  const numbered = items.filter((it) => it && it.kind !== 'status' && it.kind !== 'note');
  const current = waiting !== undefined ? waiting : waitingIndex(numbered);
  let n = 0;
  return (
    <CardFrame title={title} testId={testId} id={id}>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {items.filter(Boolean).map((item, i, arr) => {
          const last = i === arr.length - 1;
          const sep = { borderBottom: last ? 'none' : '1px solid #f3f4f6', paddingBottom: last ? 0 : 16, marginBottom: last ? 0 : 16 };
          if (item.kind === 'status' || item.kind === 'note') {
            return (
              <li key={item.id || i} id={item.id} data-testid={item.kind === 'status' ? 'checklist-status' : 'checklist-note'} style={sep}>
                {item.title ? <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 4 }}>{item.title}</div> : null}
                {item.description ? <p style={{ margin: 0, fontSize: 13.5, color: '#374151', lineHeight: 1.6 }}>{item.description}</p> : null}
                {item.content}
              </li>
            );
          }
          const idx = n; n += 1;
          const isCurrent = idx === current;
          const circle = item.done
            ? { background: CHECK_COLOURS.doneTick, color: '#fff', border: `2px solid ${CHECK_COLOURS.doneTick}` }
            : isCurrent ? { background: BLUE, color: '#fff', border: `2px solid ${BLUE}` }
              : { background: '#fff', color: '#374151', border: '2px solid #d1d5db' };
          return (
            <li key={item.id || i} id={item.id} data-testid="checklist-item" data-number={idx + 1} data-waiting={isCurrent ? 'true' : 'false'}
              data-done={item.done ? 'true' : 'false'} data-locked={item.locked ? 'true' : 'false'}
              style={item.locked ? { ...sep, opacity: 0.5 } : sep}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span aria-hidden="true" data-testid="checklist-number" style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, marginTop: 1, ...circle }}>
                  {item.done ? '✓' : idx + 1}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span data-testid="checklist-title" style={{ fontSize: 15, fontWeight: 700, color: item.done ? '#6b7280' : '#111827' }}>{item.title}</span>
                    {isCurrent && !item.locked && (
                      <span data-testid="checklist-next" style={{ fontSize: 11, fontWeight: 700, color: BLUE, border: `1px solid ${BLUE}`, borderRadius: 9999, padding: '1px 8px' }}>
                        {JOURNEY_COPY.next}
                      </span>
                    )}
                    {item.optional && !item.done && item.optionalLabel ? (
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#4b5563', border: '1px solid #e5e7eb', borderRadius: 9999, padding: '1px 8px' }}>{item.optionalLabel}</span>
                    ) : null}
                    {item.dueLabel && !item.done && !item.locked ? (
                      <span data-testid="checklist-due" data-overdue={item.overdue ? 'true' : 'false'}
                        style={{ fontSize: 11, fontWeight: 700, color: item.overdue ? CHECK_COLOURS.overdue : '#374151', border: `1px solid ${item.overdue ? CHECK_COLOURS.overdue : '#e5e7eb'}`, borderRadius: 4, padding: '1px 8px' }}>
                        {item.dueLabel}
                      </span>
                    ) : null}
                  </div>
                  {item.description ? <p style={{ margin: 0, fontSize: 13.5, color: '#374151', lineHeight: 1.6 }}>{item.description}</p> : null}
                  {item.content}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </CardFrame>
  );
}

/** A small plain box inside an item (status text, a callout). Never light blue (that marked the current item before). */
export function InlineBox({ children, tone = 'plain', testId }) {
  const tones = {
    plain: { background: '#fff', border: '1px solid #e5e7eb', color: '#111827' },
    done: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534' },
  };
  return <div data-testid={testId} style={{ marginTop: 10, borderRadius: 10, padding: '10px 12px', fontSize: 13.5, lineHeight: 1.6, ...tones[tone] }}>{children}</div>;
}

/** Scroll to an item and put the focus on its first control (the hero's next-to-do button). */
export function goToItem(id) {
  const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const ctl = el.querySelector('button:not([disabled]), a[href], input');
  if (ctl) setTimeout(() => { try { ctl.focus({ preventScroll: true }); } catch (_) { /* old browsers */ } }, 350);
}

/** 🔑 THE ONE HELP CARD (B5). "Help Center" is the link in the approved line. */
export function HelpCard({ onMessage }) {
  return (
    <div data-testid="help-card" className="mb-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#eff3fe' }}>
          <svg className="w-5 h-5" style={{ color: BLUE }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div style={{ minWidth: 0 }}>
          <h3 className="text-sm font-bold text-black mb-1">Have questions?</h3>
          {/* 🔑 Approved wording (2026-11-02): "Chat with us anytime, or visit the Help Center." — "Help Center" is the link.
              The help centre moves from Intercom to a native build at this same subdomain; the URL does not change. */}
          <p data-testid="dashboard-questions" className="text-sm" style={{ color: '#374151' }}>
            Chat with us anytime, or visit the{' '}
            <a href="https://help.godoorly.com" target="_blank" rel="noreferrer" style={{ color: BLUE, fontWeight: 600 }} className="hover:underline">Help Center</a>.
          </p>
          <ButtonRow style={{ marginTop: 12 }}>
            <PrimaryButton testId="help-message" onClick={onMessage}>{JOURNEY_COPY.messageDoorly}</PrimaryButton>
            {/* 🔑 Round 2: a computer cannot dial — the number as text there ("Call us at (512) 713-6016", constants/support); a phone or tablet
                (below 1024px) gets the tel: link. The new text is OFF until approved: until then "Call support" (tel:)
                everywhere, as approved on 11/3. */}
            {DASHBOARD_ROUND2_WORDING_APPROVED ? (
              <>
                <span className="lg:hidden"><SecondaryButton testId="help-call" href={SUPPORT_TEL}>{JOURNEY_COPY.callSupport}</SecondaryButton></span>
                <span data-testid="help-call-number" className="hidden lg:inline" style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                  {ROUND2_COPY.callNumber(SUPPORT_PHONE_DISPLAY)}
                </span>
              </>
            ) : (
              <SecondaryButton testId="help-call" href={SUPPORT_TEL}>{JOURNEY_COPY.callSupport}</SecondaryButton>
            )}
          </ButtonRow>
        </div>
      </div>
    </div>
  );
}
